import { customElement } from "lit/decorators.js";
import { Chart, type ChartDefinition } from "../Chart";

@customElement("s-latency-histogram-chart")
export class LatencyHistogramChart extends Chart {
	protected override readonly definition: ChartDefinition = {
		title: "Latency histogram",
		summary:
			"Most requests finish in the lower-latency bins, but round robin has the longest tail. Weighted round robin and least connections have shorter tails.",
		xMinimum: 0,
		xMaximum: 3750,
		xTickStep: 500,
		yMinimum: 0,
		yMaximum: 50,
		xAxisTitle: "Response time (ms)",
		yAxisTitle: "Number of requests",
		series: [
			{
				name: "Round robin (RR)",
				points: [
					{ x: 411.6666666666667, y: 3 },
					{ x: 461.6666666666667, y: 5 },
					{ x: 511.6666666666667, y: 15 },
					{ x: 561.6666666666666, y: 26 },
					{ x: 611.6666666666666, y: 21 },
					{ x: 661.6666666666666, y: 23 },
					{ x: 711.6666666666666, y: 46 },
					{ x: 761.6666666666666, y: 24 },
					{ x: 811.6666666666666, y: 38 },
					{ x: 861.6666666666666, y: 39 },
					{ x: 911.6666666666666, y: 38 },
					{ x: 961.6666666666666, y: 26 },
					{ x: 1011.6666666666666, y: 27 },
					{ x: 1061.6666666666667, y: 13 },
					{ x: 1111.6666666666667, y: 7 },
					{ x: 1161.6666666666667, y: 7 },
					{ x: 1211.6666666666667, y: 4 },
					{ x: 1261.6666666666667, y: 4 },
					{ x: 1311.6666666666667, y: 7 },
					{ x: 1361.6666666666667, y: 2 },
					{ x: 1461.6666666666667, y: 1 },
					{ x: 1511.6666666666667, y: 1 },
					{ x: 1661.6666666666667, y: 3 },
					{ x: 1711.6666666666667, y: 1 },
					{ x: 1761.6666666666667, y: 1 },
					{ x: 1811.6666666666667, y: 2 },
					{ x: 1861.6666666666667, y: 2 },
					{ x: 1961.6666666666667, y: 2 },
					{ x: 2011.6666666666667, y: 6 },
					{ x: 2061.6666666666665, y: 3 },
					{ x: 2111.6666666666665, y: 1 },
					{ x: 2161.6666666666665, y: 3 },
					{ x: 2211.6666666666665, y: 2 },
					{ x: 2261.6666666666665, y: 2 },
					{ x: 2311.6666666666665, y: 2 },
					{ x: 2361.6666666666665, y: 3 },
					{ x: 2411.6666666666665, y: 2 },
					{ x: 2461.6666666666665, y: 2 },
					{ x: 2511.6666666666665, y: 5 },
					{ x: 2561.6666666666665, y: 3 },
					{ x: 2661.6666666666665, y: 3 },
					{ x: 2711.6666666666665, y: 1 },
					{ x: 2761.6666666666665, y: 3 },
					{ x: 2811.6666666666665, y: 2 },
					{ x: 2911.6666666666665, y: 1 },
					{ x: 2961.6666666666665, y: 1 },
					{ x: 3161.6666666666665, y: 3 },
					{ x: 3461.6666666666665, y: 2 },
					{ x: 3511.6666666666665, y: 1 },
					{ x: 3711.6666666666665, y: 1 },
				],
				color: "var(--chart-orange)",
				legendLabel: "Round Robin",
			},
			{
				name: "Weighted round robin (WRR)",
				points: [
					{ x: 425, y: 1 },
					{ x: 475, y: 6 },
					{ x: 525, y: 8 },
					{ x: 575, y: 11 },
					{ x: 625, y: 15 },
					{ x: 675, y: 21 },
					{ x: 725, y: 25 },
					{ x: 775, y: 16 },
					{ x: 825, y: 18 },
					{ x: 875, y: 39 },
					{ x: 925, y: 27 },
					{ x: 975, y: 25 },
					{ x: 1025, y: 35 },
					{ x: 1075, y: 30 },
					{ x: 1125, y: 21 },
					{ x: 1175, y: 18 },
					{ x: 1225, y: 27 },
					{ x: 1275, y: 23 },
					{ x: 1325, y: 14 },
					{ x: 1375, y: 14 },
					{ x: 1425, y: 13 },
					{ x: 1475, y: 8 },
					{ x: 1525, y: 8 },
					{ x: 1625, y: 6 },
					{ x: 1675, y: 3 },
					{ x: 1725, y: 2 },
					{ x: 1775, y: 3 },
					{ x: 1825, y: 1 },
					{ x: 1875, y: 1 },
					{ x: 1925, y: 1 },
					{ x: 2125, y: 1 },
					{ x: 2275, y: 1 },
				],
				color: "var(--chart-blue)",
				texture: "dashed",
				legendLabel: "Weighted Round Robin",
			},
			{
				name: "Least connections (LC)",
				points: [
					{ x: 438.3333333333333, y: 2 },
					{ x: 488.3333333333333, y: 14 },
					{ x: 538.3333333333334, y: 11 },
					{ x: 588.3333333333334, y: 18 },
					{ x: 638.3333333333334, y: 15 },
					{ x: 688.3333333333334, y: 17 },
					{ x: 738.3333333333334, y: 40 },
					{ x: 788.3333333333334, y: 29 },
					{ x: 838.3333333333334, y: 37 },
					{ x: 888.3333333333334, y: 44 },
					{ x: 938.3333333333334, y: 39 },
					{ x: 988.3333333333334, y: 34 },
					{ x: 1038.3333333333333, y: 23 },
					{ x: 1088.3333333333333, y: 16 },
					{ x: 1138.3333333333333, y: 7 },
					{ x: 1188.3333333333333, y: 18 },
					{ x: 1238.3333333333333, y: 7 },
					{ x: 1288.3333333333333, y: 5 },
					{ x: 1338.3333333333333, y: 18 },
					{ x: 1388.3333333333333, y: 2 },
					{ x: 1438.3333333333333, y: 3 },
					{ x: 1488.3333333333333, y: 5 },
					{ x: 1538.3333333333333, y: 9 },
					{ x: 1588.3333333333333, y: 3 },
					{ x: 1638.3333333333333, y: 7 },
					{ x: 1688.3333333333333, y: 2 },
					{ x: 1738.3333333333333, y: 5 },
					{ x: 1838.3333333333333, y: 1 },
					{ x: 1938.3333333333333, y: 4 },
					{ x: 2038.3333333333333, y: 3 },
					{ x: 2088.3333333333335, y: 2 },
					{ x: 2338.3333333333335, y: 1 },
					{ x: 2388.3333333333335, y: 1 },
				],
				color: "var(--chart-green)",
				texture: "dotted",
				legendLabel: "Least Connections",
			},
		],
	};

	protected override readonly histogram = true;
}
